/** Bounded cosmetic text annotations. Offsets are UTF-16 positions in plain text,
 * never HTML, CSS, URLs, or gameplay commands. */
export interface DialogueSpan {
  start: number;
  end: number;
  bold?: boolean;
  italic?: boolean;
  size?: "small" | "normal" | "large";
  pace?: "slow" | "normal" | "quick";
  pause?: number; // seconds before this span, 0..1.5
}
export interface DialogueGlyph {
  text: string; start: number; end: number; style: Omit<DialogueSpan, "start" | "end">;
}
const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

export function dialogueGlyphs(text: string, spans: readonly DialogueSpan[] = []): DialogueGlyph[] {
  return Array.from(segmenter.segment(text), ({ segment, index }) => {
    const style: DialogueGlyph["style"] = {};
    for (const span of spans) if (index >= span.start && index < span.end) {
      if (span.bold) style.bold = true;
      if (span.italic) style.italic = true;
      if (span.size) style.size = span.size;
      if (span.pace) style.pace = span.pace;
      if (span.start === index && span.pause) style.pause = Math.max(style.pause ?? 0, span.pause);
    }
    return { text: segment, start: index, end: index + segment.length, style };
  });
}

export function validDialogueSpans(text: string, value: unknown): value is DialogueSpan[] {
  if (!Array.isArray(value) || value.length > 48) return false;
  const boundaries = new Set([text.length, ...Array.from(segmenter.segment(text), g => g.index)]);
  return value.every(s => s && typeof s === "object" && !Array.isArray(s) &&
    Number.isInteger(s.start) && Number.isInteger(s.end) && s.start >= 0 && s.end > s.start && s.end <= text.length &&
    boundaries.has(s.start) && boundaries.has(s.end) &&
    (s.bold === undefined || typeof s.bold === "boolean") && (s.italic === undefined || typeof s.italic === "boolean") &&
    (s.size === undefined || ["small", "normal", "large"].includes(s.size)) &&
    (s.pace === undefined || ["slow", "normal", "quick"].includes(s.pace)) &&
    (s.pause === undefined || (Number.isFinite(s.pause) && s.pause >= 0 && s.pause <= 1.5)));
}

export function glyphDelay(glyph: DialogueGlyph, cps: number): number {
  const pace = glyph.style.pace === "slow" ? 1.65 : glyph.style.pace === "quick" ? 0.7 : 1;
  return pace * (glyph.style.bold ? 1.15 : 1) / cps +
    (/[.!?…]/u.test(glyph.text) ? 0.2 : /[,;:—]/u.test(glyph.text) ? 0.1 : /\n/u.test(glyph.text) ? 0.25 : 0);
}

export interface DialogueLine { glyphs: DialogueGlyph[]; start: number; end: number }
export interface DialoguePage { lines: DialogueLine[]; start: number; end: number }

/** Lay out complete words before revealing any letters. A long unbroken token alone
 * may split at grapheme boundaries. Consecutive paragraph breaks share one line break. */
export function paginateDialogue(text: string, spans: readonly DialogueSpan[], width: number,
  measure: (glyphs: readonly DialogueGlyph[]) => number): DialoguePage[] {
  const glyphs = dialogueGlyphs(text, spans), lines: DialogueLine[] = [];
  let line: DialogueGlyph[] = [];
  const flush = () => {
    while (line.length && /^\s+$/u.test(line.at(-1)!.text)) line.pop();
    if (line.length) lines.push({ glyphs: line, start: line[0].start, end: line.at(-1)!.end });
    line = [];
  };
  const tokens: DialogueGlyph[][] = [];
  for (const glyph of glyphs) {
    const space = /^\s+$/u.test(glyph.text);
    if (space || !tokens.length || /^\s+$/u.test(tokens.at(-1)![0].text)) tokens.push([glyph]);
    else tokens.at(-1)!.push(glyph);
  }
  const limit = Math.max(1, width);
  for (const token of tokens) {
    if (token[0].text.includes("\n")) { flush(); continue; }
    if (/^\s+$/u.test(token[0].text)) { if (line.length) line.push(...token); continue; }
    if (line.length && measure([...line, ...token]) > limit) flush();
    if (measure(token) <= limit) line.push(...token);
    else for (const glyph of token) {
      if (line.length && measure([...line, glyph]) > limit) flush();
      line.push(glyph);
    }
  }
  flush();
  const pages: DialoguePage[] = [];
  for (let i = 0; i < lines.length; i += 2) {
    const pair = lines.slice(i, i + 2);
    pages.push({ lines: pair, start: pair[0].start, end: pair.at(-1)!.end });
  }
  if (pages.length) pages.at(-1)!.end = text.length;
  return pages.length ? pages : [{ lines: [], start: 0, end: text.length }];
}
